"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeElectrumScriptHash = exports.ElectrumClient = void 0;
const bitcoinjs_lib_1 = require("bitcoinjs-lib");
const p_timeout_1 = __importDefault(require("p-timeout"));
const bitcoin_1 = require("../bitcoin");
const electrum_client_js_1 = __importDefault(require("electrum-client-js"));
const ethers_1 = require("ethers");
const url_1 = require("url");
const url_parse_1 = __importDefault(require("url-parse"));
const utils_1 = require("../utils");
const mainnet_json_1 = __importDefault(require("./urls/mainnet.json"));
const testnet_json_1 = __importDefault(require("./urls/testnet.json"));
const browserURL = typeof window !== "undefined" && window.URL;
const URL = url_1.URL !== null && url_1.URL !== void 0 ? url_1.URL : (browserURL || url_parse_1.default);
/**
 * Electrum-based implementation of the Bitcoin client.
 */
class ElectrumClient {
    constructor(credentials, options, totalRetryAttempts = 3, retryBackoffStep = 10000, // 10 seconds
    connectionTimeout = 20000 // 20 seconds
    ) {
        this.credentials = credentials;
        this.options = options;
        this.totalRetryAttempts = totalRetryAttempts;
        this.retryBackoffStep = retryBackoffStep;
        this.connectionTimeout = connectionTimeout;
    }
    /**
     * Creates an Electrum client instance from a URL.
     * @param url - Connection URL or list of URLs.
     * @param options - Additional options used by the Electrum server.
     * @param totalRetryAttempts - Number of retries for requests sent to Electrum
     *        server.
     * @param retryBackoffStep - Initial backoff step in milliseconds that will
     *        be increased exponentially for subsequent retry attempts.
     * @param connectionTimeout - Timeout for a single try of connection establishment.
     * @returns Electrum client instance.
     */
    static fromUrl(url, options, totalRetryAttempts = 3, retryBackoffStep = 1000, // 10 seconds
    connectionTimeout = 20000 // 20 seconds
    ) {
        let credentials;
        if (Array.isArray(url)) {
            credentials = url.map(this.parseElectrumCredentials);
        }
        else {
            credentials = [this.parseElectrumCredentials(url)];
        }
        return new ElectrumClient(credentials, options, totalRetryAttempts, retryBackoffStep, connectionTimeout);
    }
    /**
     * Creates an Electrum client instance using a default config for the given
     * Bitcoin network.
     * @param network Bitcoin network the instance should be created for.
     * @returns Electrum client instance.
     */
    static fromDefaultConfig(network) {
        let file;
        switch (network) {
            case bitcoin_1.BitcoinNetwork.Mainnet:
                file = mainnet_json_1.default;
                break;
            case bitcoin_1.BitcoinNetwork.Testnet:
                file = testnet_json_1.default;
                break;
            default:
                throw new Error("No default Electrum for given network");
        }
        return ElectrumClient.fromUrl(file.urls);
    }
    /**
     * Create Electrum credentials by parsing an URL.
     * @param url - URL to be parsed.
     * @returns Electrum credentials object.
     */
    static parseElectrumCredentials(url) {
        const urlObj = new URL(url);
        return {
            host: urlObj.hostname,
            port: Number.parseInt(urlObj.port, 10),
            protocol: urlObj.protocol.replace(":", ""),
        };
    }
    /**
     * Initiates an Electrum connection and uses it to feed the given action.
     * Closes the connection regardless of the action outcome.
     * @param action - Action that makes use of the Electrum connection.
     * @returns Promise holding the outcome.
     */
    async withElectrum(action) {
        const connect = async (credentials) => {
            const electrum = new electrum_client_js_1.default(credentials.host, credentials.port, credentials.protocol, this.options);
            await this.withBackoffRetrier()(async () => {
                // FIXME: Connection timeout should be a property of the Electrum client.
                // Since it's not configurable in `electrum-client-js` we add timeout
                // as a workaround here.
                return (0, p_timeout_1.default)((async () => {
                    try {
                        await electrum.connect("tbtc-v2", "1.4.2");
                        await electrum.server_ping();
                        return;
                    }
                    catch (error) {
                        throw new Error(`Electrum server connection failure: [${error}]`);
                    }
                })(), this.connectionTimeout, `timed out on electrum connect after ${this.connectionTimeout} ms`);
            });
            return electrum;
        };
        let electrum = undefined;
        for (const credentials of this.credentials) {
            try {
                electrum = await connect(credentials);
                break;
            }
            catch (err) {
                console.warn(`failed to connect to electrum server: [${credentials.protocol}://${credentials.host}:${credentials.port}]: ${err}`);
            }
        }
        if (!electrum) {
            throw new Error("failed to connect to any of defined electrum servers");
        }
        try {
            return await action(electrum);
        }
        catch (error) {
            throw new Error(`Electrum action failure: [${error}]`);
        }
        finally {
            electrum.close();
        }
    }
    /**
     * Initiates a backoff retrier.
     * @returns A function that can retry any function.
     */
    withBackoffRetrier() {
        return (0, utils_1.backoffRetrier)(this.totalRetryAttempts, this.retryBackoffStep);
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {BitcoinClient#getNetwork}
     */
    getNetwork() {
        return this.withElectrum(async (electrum) => {
            const { genesis_hash: genesisHash } = await this.withBackoffRetrier()(async () => {
                return await electrum.server_features();
            });
            if (!genesisHash) {
                throw new Error("server didn't return the 'genesis_hash' property from `server.features` request");
            }
            return bitcoin_1.BitcoinNetwork.fromGenesisHash(utils_1.Hex.from(genesisHash));
        });
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {BitcoinClient#findAllUnspentTransactionOutputs}
     */
    findAllUnspentTransactionOutputs(address) {
        return this.withElectrum(async (electrum) => {
            const bitcoinNetwork = await this.getNetwork();
            const script = bitcoin_1.BitcoinAddressConverter.addressToOutputScript(address, bitcoinNetwork);
            const unspentTransactions = await this.withBackoffRetrier()(async () => {
                return await electrum.blockchain_scripthash_listunspent(computeElectrumScriptHash(script));
            });
            return unspentTransactions.reverse().map((tx) => ({
                transactionHash: bitcoin_1.BitcoinTxHash.from(tx.tx_hash),
                outputIndex: tx.tx_pos,
                value: ethers_1.BigNumber.from(tx.value),
            }));
        });
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {BitcoinClient#getTransactionHistory}
     */
    getTransactionHistory(address, limit) {
        return this.withElectrum(async (electrum) => {
            const bitcoinNetwork = await this.getNetwork();
            const script = bitcoin_1.BitcoinAddressConverter.addressToOutputScript(address, bitcoinNetwork);
            let historyItems = await this.withBackoffRetrier()(async () => {
                return await electrum.blockchain_scripthash_getHistory(computeElectrumScriptHash(script));
            });
            // According to https://electrumx.readthedocs.io/en/latest/protocol-methods.html#blockchain-scripthash-get-history
            // unconfirmed items living in the mempool are appended at the end of the
            // returned list and their height value is either -1 or 0. That means
            // we need to take all items with height >0 to obtain a confirmed txs
            // history.
            historyItems = historyItems.filter((item) => item.height > 0);
            // The list returned from blockchain.scripthash.get_history is sorted by
            // the block height in the ascending order though we are sorting it
            // again just in case (e.g. API contract changes).
            historyItems = historyItems.sort((item1, item2) => item1.height - item2.height);
            if (typeof limit !== "undefined" &&
                limit > 0 &&
                historyItems.length > limit) {
                historyItems = historyItems.slice(-limit);
            }
            const transactions = historyItems.map((item) => this.getTransaction(bitcoin_1.BitcoinTxHash.from(item.tx_hash)));
            return Promise.all(transactions);
        });
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {BitcoinClient#getTransaction}
     */
    getTransaction(transactionHash) {
        return this.withElectrum(async (electrum) => {
            // We cannot use `blockchain_transaction_get` with `verbose = true` argument
            // to get the the transaction details as Esplora/Electrs doesn't support verbose
            // transactions.
            // See: https://github.com/Blockstream/electrs/pull/36
            const rawTransaction = await this.withBackoffRetrier()(async () => {
                return await electrum.blockchain_transaction_get(transactionHash.toString(), false);
            });
            if (!rawTransaction) {
                throw new Error(`Transaction not found`);
            }
            // Decode the raw transaction.
            const transaction = bitcoinjs_lib_1.Transaction.fromHex(rawTransaction);
            const inputs = transaction.ins.map((input) => ({
                transactionHash: bitcoin_1.BitcoinTxHash.from(input.hash).reverse(),
                outputIndex: input.index,
                scriptSig: utils_1.Hex.from(input.script),
            }));
            const outputs = transaction.outs.map((output, i) => ({
                outputIndex: i,
                value: ethers_1.BigNumber.from(output.value),
                scriptPubKey: utils_1.Hex.from(output.script),
            }));
            return {
                transactionHash: bitcoin_1.BitcoinTxHash.from(transaction.getId()),
                inputs: inputs,
                outputs: outputs,
            };
        });
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {BitcoinClient#getRawTransaction}
     */
    getRawTransaction(transactionHash) {
        return this.withElectrum(async (electrum) => {
            const transaction = await this.withBackoffRetrier()(async () => {
                return await electrum.blockchain_transaction_get(transactionHash.toString(), false);
            });
            return {
                transactionHex: transaction,
            };
        });
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {BitcoinClient#getTransactionConfirmations}
     */
    getTransactionConfirmations(transactionHash) {
        // We cannot use `blockchain_transaction_get` with `verbose = true` argument
        // to get the the transaction details as Esplora/Electrs doesn't support verbose
        // transactions.
        // See: https://github.com/Blockstream/electrs/pull/36
        return this.withElectrum(async (electrum) => {
            const rawTransaction = await this.withBackoffRetrier()(async () => {
                return await electrum.blockchain_transaction_get(transactionHash.toString(), false);
            });
            // Decode the raw transaction.
            const transaction = bitcoinjs_lib_1.Transaction.fromHex(rawTransaction);
            // As a workaround for the problem described in https://github.com/Blockstream/electrs/pull/36
            // we need to calculate the number of confirmations based on the latest
            // block height and block height of the transaction.
            // Electrum protocol doesn't expose a function to get the transaction's block
            // height (other that the `GetTransaction` that is unsupported by Esplora/Electrs).
            // To get the block height of the transaction we query the history of transactions
            // for the output script hash, as the history contains the transaction's block
            // height.
            // Initialize txBlockHeigh with minimum int32 value to identify a problem when
            // a block height was not found in a history of any of the script hashes.
            //
            // The history is expected to return a block height for confirmed transaction.
            // If a transaction is unconfirmed (is still in the mempool) the height will
            // have a value of `0` or `-1`.
            let txBlockHeight = Math.min();
            for (const output of transaction.outs) {
                const scriptHash = bitcoin_1.BitcoinHashUtils.computeSha256(utils_1.Hex.from(output.script)).toBuffer();
                const scriptHashHistory = await this.withBackoffRetrier()(async () => {
                    return await electrum.blockchain_scripthash_getHistory(scriptHash.reverse().toString("hex"));
                });
                const tx = scriptHashHistory.find((t) => t.tx_hash === transactionHash.toString());
                if (tx) {
                    txBlockHeight = tx.height;
                    break;
                }
            }
            // History querying didn't come up with the transaction's block height. Return
            // an error.
            if (txBlockHeight === Math.min()) {
                throw new Error("failed to find the transaction block height in script hashes' histories");
            }
            // If the block height is greater than `0` the transaction is confirmed.
            if (txBlockHeight > 0) {
                const latestBlockHeight = await this.latestBlockHeight();
                if (latestBlockHeight >= txBlockHeight) {
                    // Add `1` to the calculated difference as if the transaction block
                    // height equals the latest block height the transaction is already
                    // confirmed, so it has one confirmation.
                    return latestBlockHeight - txBlockHeight + 1;
                }
            }
            return 0;
        });
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {BitcoinClient#getTxHashesForPublicKeyHash}
     */
    getTxHashesForPublicKeyHash(publicKeyHash) {
        return this.withElectrum(async (electrum) => {
            const bitcoinNetwork = await this.getNetwork();
            const getConfirmedHistory = async (witnessAddress) => {
                const address = bitcoin_1.BitcoinAddressConverter.publicKeyHashToAddress(publicKeyHash, witnessAddress, bitcoinNetwork);
                const script = bitcoin_1.BitcoinAddressConverter.addressToOutputScript(address, bitcoinNetwork);
                let historyItems = await this.withBackoffRetrier()(async () => {
                    return await electrum.blockchain_scripthash_getHistory(computeElectrumScriptHash(script));
                });
                // According to https://electrumx.readthedocs.io/en/latest/protocol-methods.html#blockchain-scripthash-get-history
                // unconfirmed items living in the mempool are appended at the end of the
                // returned list and their height value is either -1 or 0. That means
                // we need to take all items with height >0 to obtain a confirmed txs
                // history.
                historyItems = historyItems.filter((item) => item.height > 0);
                // The list returned from blockchain.scripthash.get_history is sorted by
                // the block height in the ascending order though we are sorting it
                // again just in case (e.g. API contract changes).
                historyItems = historyItems.sort((a, b) => a.height - b.height);
                return historyItems;
            };
            const p2pkhItems = await getConfirmedHistory(false);
            const p2wpkhItems = await getConfirmedHistory(true);
            const items = [...p2pkhItems, ...p2wpkhItems];
            items.sort((a, b) => a.height - b.height);
            return items.map((item) => bitcoin_1.BitcoinTxHash.from(item.tx_hash));
        });
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {BitcoinClient#latestBlockHeight}
     */
    latestBlockHeight() {
        return this.withElectrum(async (electrum) => {
            const { height } = await this.withBackoffRetrier()(async () => {
                return await electrum.blockchain_headers_subscribe();
            });
            return height;
        });
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {BitcoinClient#getHeadersChain}
     */
    getHeadersChain(blockHeight, chainLength) {
        return this.withElectrum(async (electrum) => {
            const { hex } = await this.withBackoffRetrier()(async () => {
                return await electrum.blockchain_block_headers(blockHeight, chainLength + 1);
            });
            return utils_1.Hex.from(hex);
        });
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {BitcoinClient#getTransactionMerkle}
     */
    getTransactionMerkle(transactionHash, blockHeight) {
        return this.withElectrum(async (electrum) => {
            const merkle = await this.withBackoffRetrier()(async () => {
                return await electrum.blockchain_transaction_getMerkle(transactionHash.toString(), blockHeight);
            });
            return {
                blockHeight: merkle.block_height,
                merkle: merkle.merkle.map((m) => utils_1.Hex.from(m)),
                position: merkle.pos,
            };
        });
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {BitcoinClient#broadcast}
     */
    broadcast(transaction) {
        return this.withElectrum(async (electrum) => {
            await this.withBackoffRetrier()(async () => {
                return await electrum.blockchain_transaction_broadcast(transaction.transactionHex);
            });
        });
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {BitcoinClient#getCoinbaseTxHash}
     */
    getCoinbaseTxHash(blockHeight) {
        return this.withElectrum(async (electrum) => {
            const txHash = await this.withBackoffRetrier()(async () => {
                return await electrum.request("blockchain.transaction.id_from_pos", [
                    blockHeight,
                    0,
                    false,
                ]);
            });
            return bitcoin_1.BitcoinTxHash.from(txHash);
        });
    }
}
exports.ElectrumClient = ElectrumClient;
/**
 * Converts a Bitcoin script to an Electrum script hash. See
 * [Electrum protocol]{@link https://electrumx.readthedocs.io/en/stable/protocol-basics.html#script-hashes}
 * @param script - Bitcoin script as hex string
 * @returns Electrum script hash as a hex string.
 */
function computeElectrumScriptHash(script) {
    return bitcoin_1.BitcoinHashUtils.computeSha256(script).reverse().toString();
}
exports.computeElectrumScriptHash = computeElectrumScriptHash;
//# sourceMappingURL=client.js.map